import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import config from "@/config";
import useStateHandler from "@/hooks/useStateHandler";
import {
  mergeArtworkSocialData,
  normaliseArtworkSocialSummary,
} from "@/pages/artwork/utils/artworkSocialSummary.utils";
import fetchWrapper from "@/services/fetchWrapper.service";

const inFlightRequests = new Map();

function buildRequestedSections({
  shouldLoadSocialSummary,
  shouldLoadViewerContext,
  shouldLoadPurchaseContext,
}) {
  return [
    shouldLoadSocialSummary || shouldLoadViewerContext ? "social" : null,
    shouldLoadViewerContext ? "viewer" : null,
    shouldLoadPurchaseContext ? "purchase" : null,
  ].filter(Boolean);
}

export default function useArtworkViewContext({
  artworkSlug,
  isActive,
  socialSummary: initialSocialSummary,
  viewerSessionId,
  soldPriceAccessStatus,
  shouldLoadViewerContext,
  shouldLoadPurchaseContext,
}) {
  const { cacheHandler } = useStateHandler();
  const navigate = useNavigate();
  const socialSummaryCacheKey = `artwork_${artworkSlug}_social_summary`;
  const cachedSocialSummary = normaliseArtworkSocialSummary(
    cacheHandler.getCacheValue(socialSummaryCacheKey)?.data
  );
  const serverSocialSummary = useMemo(
    () => normaliseArtworkSocialSummary(initialSocialSummary),
    [initialSocialSummary]
  );
  const socialSummary = cachedSocialSummary ?? serverSocialSummary;
  const shouldLoadSocialSummary = !socialSummary;
  const requestedSections = useMemo(() => buildRequestedSections({
    shouldLoadSocialSummary,
    shouldLoadViewerContext,
    shouldLoadPurchaseContext,
  }), [
    shouldLoadPurchaseContext,
    shouldLoadSocialSummary,
    shouldLoadViewerContext,
  ]);
  const sectionsKey = requestedSections.join(",");
  const socialMode = shouldLoadSocialSummary ? "full" : (shouldLoadViewerContext ? "viewer" : null);
  const cacheKey = `artwork_${artworkSlug}_view_context_${viewerSessionId || "guest"}_${soldPriceAccessStatus || "unknown"}_${sectionsKey}_${socialMode || "none"}`;
  const requestParams = new URLSearchParams({ sections: sectionsKey });
  if (socialMode === "viewer") {
    requestParams.set("social_mode", "viewer");
  }
  const requestUrl = `${config.apis.api.url}/artwork/${artworkSlug}/view-context/?${requestParams}`;
  const [viewContextData, setViewContextData] = useState(() => (
    cacheHandler.getCacheValue(cacheKey)?.data ?? null
  ));
  const [loading, setLoading] = useState(() => !viewContextData);

  useEffect(() => {
    if (!cachedSocialSummary && serverSocialSummary) {
      cacheHandler.storeInCache(socialSummaryCacheKey, serverSocialSummary, {
        type: "@cache/no-expiration",
      });
    }
  }, [cacheHandler, cachedSocialSummary, serverSocialSummary, socialSummaryCacheKey]);

  useEffect(() => {
    let cancelled = false;
    const cachedValue = cacheHandler.getCacheValue(cacheKey);

    if (cachedValue && !cachedValue.isDataInvalid) {
      setViewContextData(cachedValue.data);
      setLoading(false);
      return undefined;
    }

    setViewContextData(null);
    if (!isActive || !artworkSlug || !sectionsKey) {
      setLoading(false);
      return undefined;
    }

    setLoading(true);
    let request = inFlightRequests.get(cacheKey);
    if (!request) {
      request = fetchWrapper.get(requestUrl, { optionalToken: true });
      inFlightRequests.set(cacheKey, request);
    }

    request
      .then(({ response, data }) => {
        if (cancelled) {
          return;
        }

        if (response.ok) {
          const responseSocialSummary = normaliseArtworkSocialSummary(data?.social);
          const currentSocialSummary = normaliseArtworkSocialSummary(
            cacheHandler.getCacheValue(socialSummaryCacheKey)?.data
          );
          if (!currentSocialSummary && responseSocialSummary) {
            cacheHandler.storeInCache(socialSummaryCacheKey, responseSocialSummary, {
              type: "@cache/no-expiration",
            });
          }
          cacheHandler.storeInCache(cacheKey, data, { type: "@cache/dynamic" });
          setViewContextData(data);
          setLoading(false);
          return;
        }

        if ([401, 403].includes(response.status)) {
          navigate("/", { replace: true });
        }
        setLoading(false);
      })
      .catch(() => {
        if (!cancelled) {
          setLoading(false);
        }
      })
      .finally(() => {
        if (inFlightRequests.get(cacheKey) === request) {
          inFlightRequests.delete(cacheKey);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [artworkSlug, cacheHandler, cacheKey, isActive, navigate, requestUrl, sectionsKey]);

  const setSocialsData = useCallback((nextSocialData) => {
    const currentSocialData = mergeArtworkSocialData(socialSummary, viewContextData?.social);
    const social = typeof nextSocialData === "function"
      ? nextSocialData(currentSocialData)
      : nextSocialData;
    const nextSocialSummary = normaliseArtworkSocialSummary(social);
    const nextViewContextData = {
      ...(viewContextData ?? {}),
      social,
    };

    setViewContextData(nextViewContextData);
    if (nextSocialSummary) {
      cacheHandler.storeInCache(socialSummaryCacheKey, nextSocialSummary, {
        type: "@cache/no-expiration",
      });
    }
    cacheHandler.storeInCache(cacheKey, nextViewContextData, { type: "@cache/dynamic" });
  }, [cacheHandler, cacheKey, socialSummary, socialSummaryCacheKey, viewContextData]);

  const contextMatchesArtwork = viewContextData?.slug === artworkSlug
    && (!soldPriceAccessStatus || viewContextData?.sold_price_access?.status === soldPriceAccessStatus);

  return {
    loading,
    socialsData: mergeArtworkSocialData(socialSummary, viewContextData?.social),
    setSocialsData,
    viewerContext: contextMatchesArtwork ? viewContextData?.viewer_context ?? null : null,
    purchaseContext: contextMatchesArtwork ? viewContextData?.purchase_context ?? null : null,
  };
}
